import React, { useState, useEffect } from "react";

import { Amplify } from "aws-amplify";
import { signIn, signUp, confirmSignUp, fetchAuthSession, signOut as amplifySignOut } from "aws-amplify/auth";
import awsconfig from "../aws-exports";
import "./Login.css";

import Main from "./Main";
import About from "./About";
import SEO from "./SEO";
import Link from "./Link";

import theDailyCompLogo from "../assets/Main_logo_proto1.svg";

// Import icons for features
import {
    FaTrophy,
    FaUsers,
    FaBrain,
    FaCalendarAlt,
    FaStar,
    FaGamepad,
    FaEye,
    FaEyeSlash
} from "react-icons/fa";

// Configure Amplify once - moved inside component to avoid module-level execution issues
// Amplify.configure(awsconfig);

const Login = () => {
    // Configure Amplify on first render
    React.useEffect(() => {
        Amplify.configure(awsconfig);
    }, []);
    
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(() => {
        // Initialize based on current URL path
        const path = window.location.pathname;
        return path === '/about' ? 'about' : 'login';
    });
    const [authMode, setAuthMode] = useState('signin'); // 'signin', 'signup', 'confirm'
    const [showPassword, setShowPassword] = useState(false);
    // Check if it's mobile on initial load
    const [showAuthForm, setShowAuthForm] = useState(window.innerWidth > 768); // Show by default on desktop
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmationCode: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Check if user is already authenticated
    useEffect(() => {
        checkAuthState();
        
        // Handle window resize to show/hide auth form appropriately
        const handleResize = () => {
            if (window.innerWidth > 768) {
                setShowAuthForm(true); // Always show on desktop
            } else {
                // On mobile, only show if already visible
                // This prevents hiding it when user has it open
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle browser back/forward buttons
    useEffect(() => {
        const handlePopState = () => {
            const path = window.location.pathname;
            setCurrentPage(path === '/about' ? 'about' : 'login');
            window.scrollTo(0, 0); // Scroll to top when using browser navigation
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // Navigation function to change pages and update URL
    const navigateToPage = (page) => {
        const newPath = page === 'about' ? '/about' : '/';
        window.history.pushState({}, '', newPath);
        setCurrentPage(page);
        window.scrollTo(0, 0); // Scroll to top of page
    };

    const checkAuthState = async () => {
        try {
            const session = await fetchAuthSession();
            if (session.tokens?.accessToken) {
                // User is authenticated, set user data from the session
                setUser({
                    username: session.tokens.accessToken.payload.username,
                    userId: session.tokens.accessToken.payload.sub,
                    email: session.tokens.accessToken.payload.email,
                    ...session
                });
            } else {
                setUser(null);
            }
        } catch (error) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (authMode === 'signup') {
            if (!formData.username.trim()) {
                newErrors.username = 'Username is required';
            }
            if (!formData.email.trim()) {
                newErrors.email = 'Email is required';
            } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
                newErrors.email = 'Please enter a valid email address';
            }
        } else if (authMode === 'signin') {
            if (!formData.username.trim()) {
                newErrors.username = 'Username is required';
            }
        }

        if (authMode !== 'confirm') {
            if (!formData.password.trim()) {
                newErrors.password = 'Password is required';
            } else if (formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters';
            }
        }

        if (authMode === 'confirm') {
            if (!formData.confirmationCode.trim()) {
                newErrors.confirmationCode = 'Confirmation code is required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSignUp = async () => {
        try {
            await signUp({
                username: formData.username,
                password: formData.password,
                options: {
                    userAttributes: {
                        email: formData.email,
                    }
                }
            });
            setAuthMode('confirm');
            setErrors({});
        } catch (error) {
            setErrors({ general: error.message });
        }
    };

    const handleSignIn = async () => {
        try {
            const { isSignedIn, nextStep } = await signIn({
                username: formData.username,
                password: formData.password
            });

            if (isSignedIn) {
                const session = await fetchAuthSession();
                if (session.tokens?.accessToken) {
                    setUser({
                        username: session.tokens.accessToken.payload.username,
                        userId: session.tokens.accessToken.payload.sub,
                        email: session.tokens.accessToken.payload.email,
                        ...session
                    });
                }
            }
        } catch (error) {
            setErrors({ general: error.message });
        }
    };

    const handleConfirmSignUp = async () => {
        try {
            await confirmSignUp({
                username: formData.username,
                confirmationCode: formData.confirmationCode
            });

            // After confirmation, sign the user in
            await handleSignIn();
        } catch (error) {
            setErrors({ general: error.message });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);
        setErrors({});

        try {
            if (authMode === 'signup') {
                await handleSignUp();
            } else if (authMode === 'signin') {
                await handleSignIn();
            } else if (authMode === 'confirm') {
                await handleConfirmSignUp();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await amplifySignOut();
            setUser(null);
            setFormData({
                username: '',
                email: '',
                password: '',
                confirmationCode: ''
            });
            setAuthMode('signin');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const switchAuthMode = (mode) => {
        setAuthMode(mode);
        setErrors({});
        setShowAuthForm(true); // Show auth form when switching modes
        setFormData(prev => ({
            ...prev,
            password: '',
            confirmationCode: ''
        }));
    };

    if (loading) {
        return (
            <div className="login-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    // If user is authenticated, show Main component
    if (user) {
        return <Main signOut={handleSignOut} user={user} />;
    }

    // If viewing About page
    if (currentPage === 'about') {
        return <About />;
    }

    return (
        <div className="login-container">
            <SEO 
                title="Daily Word Games - Play Free Online Word Challenges & Competitions"
                description="Play free daily word games online! Challenge friends in multiplayer word competitions with Scrabble, Pokémon, and Countries modes. Create custom tournaments and compete in word challenges daily."
                keywords="daily word games, word challenges, multiplayer word games, online word competitions, daily word challenge, word game competitions, competitive word games, free word games online"
                url="https://thedailycomp.com/"
            />
            
            <div className="login-content">
                {/* Welcome Section */}
                <div className="login-welcome">
                    <div className="welcome-header">
                        <img
                            alt="Daily Word Games - The Daily Comp Logo"
                            src={theDailyCompLogo}
                            className="welcome-logo"
                        />
                        <h1 className="welcome-heading">
                            Daily Word Games - Challenge Yourself Today
                        </h1>
                        <h2 className="welcome-subtitle">
                            Free Online Multiplayer Word Competitions
                        </h2>

                        {/* Mobile Login Buttons - Positioned after subtitle */}
                        <div className="mobile-auth-buttons">
                            <button 
                                className="mobile-login-btn primary"
                                onClick={() => {
                                    setAuthMode('signin');
                                    setShowAuthForm(true);
                                }}
                            >
                                Sign In
                            </button>
                            <button 
                                className="mobile-login-btn secondary"
                                onClick={() => {
                                    setAuthMode('signup');
                                    setShowAuthForm(true);
                                }}
                            >
                                Create Account
                            </button>
                        </div>
                    </div>

                    <p className="welcome-description">
                        Play <strong>daily word games</strong> and <strong>word challenges</strong> online! 
                        Compete with friends in <strong>multiplayer word competitions</strong> featuring Scrabble, Pokémon, 
                        and Countries modes. Play <strong>competitive word games</strong> daily, create custom tournaments, 
                        climb leaderboards, and test your vocabulary skills. Free to play - start your 
                        <strong> online word competition</strong> journey today!
                    </p>

                    <div className="feature-highlights">
                        <div className="feature-item">
                            <FaTrophy className="feature-icon" />
                            <div className="feature-content">
                                <h3>Daily Word Game Competitions</h3>
                                <p>New word challenges every day with themed competitions and varying difficulty levels</p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <FaUsers className="feature-icon" />
                            <div className="feature-content">
                                <h3>Multiplayer Word Games</h3>
                                <p>Challenge friends, family, and coworkers in private word competitions</p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <FaBrain className="feature-icon" />
                            <div className="feature-content">
                                <h3>Competitive Word Game Modes</h3>
                                <p>Scrabble, Pokémon, Countries, and more themed word game challenges</p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <FaCalendarAlt className="feature-icon" />
                            <div className="feature-content">
                                <h3>Online Word Tournaments</h3>
                                <p>Create custom word competitions with your own dates and rules</p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <FaStar className="feature-icon" />
                            <div className="feature-content">
                                <h3>Leaderboards & Stats</h3>
                                <p>Track your progress and compete for the top spots</p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <FaGamepad className="feature-icon" />
                            <div className="feature-content">
                                <h3>Mobile Optimized</h3>
                                <p>Play anywhere, anytime on any device with seamless experience</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Custom Auth Section */}
                <div className="login-auth-container">
                    {/* Auth Form - Always visible on desktop, conditional on mobile */}
                    <div className={`auth-wrapper ${showAuthForm ? 'auth-visible' : 'auth-hidden'}`}>
                        {showAuthForm && (
                            <button 
                                className="close-auth-btn"
                                onClick={() => setShowAuthForm(false)}
                                aria-label="Close login form"
                            >
                                ×
                            </button>
                        )}
                        
                        <div className="auth-header">
                            <img
                                alt="The daily comp logo"
                                src={theDailyCompLogo}
                                className="auth-logo"
                            />
                            <p className="auth-title">
                                {authMode === 'signin' && 'Welcome Back!'}
                                {authMode === 'signup' && 'Join The Daily Comp'}
                                {authMode === 'confirm' && 'Confirm Your Account'}
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="auth-form">
                            {errors.general && (
                                <div className="error-message">
                                    {errors.general}
                                </div>
                            )}

                            {authMode === 'confirm' ? (
                                <div className="form-group">
                                    <label htmlFor="confirmationCode">Confirmation Code</label>
                                    <input
                                        type="text"
                                        id="confirmationCode"
                                        name="confirmationCode"
                                        value={formData.confirmationCode}
                                        onChange={handleInputChange}
                                        placeholder="Enter the code sent to your email"
                                        className={errors.confirmationCode ? 'error' : ''}
                                    />
                                    {errors.confirmationCode && <span className="field-error">{errors.confirmationCode}</span>}
                                    <p className="helper-text">
                                        Check your email for a confirmation code from AWS Cognito
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="form-group">
                                        <label htmlFor="username">Username</label>
                                        <input
                                            type="text"
                                            id="username"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleInputChange}
                                            placeholder="Enter your username"
                                            className={errors.username ? 'error' : ''}
                                        />
                                        {errors.username && <span className="field-error">{errors.username}</span>}
                                    </div>

                                    {authMode === 'signup' && (
                                        <div className="form-group">
                                            <label htmlFor="email">Email</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="Enter your email"
                                                className={errors.email ? 'error' : ''}
                                            />
                                            {errors.email && <span className="field-error">{errors.email}</span>}
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label htmlFor="password">Password</label>
                                        <div className="password-input-wrapper">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                id="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                placeholder="Enter your password"
                                                className={errors.password ? 'error' : ''}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                        {errors.password && <span className="field-error">{errors.password}</span>}
                                        {authMode === 'signup' && (
                                            <p className="helper-text">
                                                Password must be at least 8 characters with uppercase, lowercase, number, and symbol
                                            </p>
                                        )}
                                    </div>
                                </>
                            )}

                            <button
                                type="submit"
                                className="auth-submit-btn"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <span className="loading-text">
                                        <div className="inline-spinner"></div>
                                        {authMode === 'signin' && 'Signing In...'}
                                        {authMode === 'signup' && 'Creating Account...'}
                                        {authMode === 'confirm' && 'Confirming...'}
                                    </span>
                                ) : (
                                    <>
                                        {authMode === 'signin' && 'Sign In'}
                                        {authMode === 'signup' && 'Create Account'}
                                        {authMode === 'confirm' && 'Confirm Account'}
                                    </>
                                )}
                            </button>
                        </form>

                        <div className="auth-switch">
                            {authMode === 'signin' ? (
                                <p>
                                    Don't have an account?{' '}
                                    <button
                                        type="button"
                                        className="link-btn"
                                        onClick={() => switchAuthMode('signup')}
                                    >
                                        Sign up here
                                    </button>
                                </p>
                            ) : authMode === 'signup' ? (
                                <p>
                                    Already have an account?{' '}
                                    <button
                                        type="button"
                                        className="link-btn"
                                        onClick={() => switchAuthMode('signin')}
                                    >
                                        Sign in here
                                    </button>
                                </p>
                            ) : (
                                <p>
                                    Need to sign in instead?{' '}
                                    <button
                                        type="button"
                                        className="link-btn"
                                        onClick={() => switchAuthMode('signin')}
                                    >
                                        Go to sign in
                                    </button>
                                </p>
                            )}
                        </div>

                        <div className="auth-footer">
                            <p className="auth-footer-text">
                                Join other word game enthusiasts competing daily!
                                Create your free account or sign in to start challenging friends and climbing the leaderboards.
                            </p>
                            <div className="auth-footer-links">
                                <Link to="about" className="footer-link-btn">
                                    About & How to Play
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;